import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { getFromBrandConfig } from 'brand-config';
import { selectOfferPlanCode } from 'services/offer/offerSelectors';
import {
  selectAvailableOffers,
  selectAvailableOffersLoaded,
  selectPlanChangeEligibility,
  selectPlanChangeEligibilityLoaded,
} from 'services/plan/planSelectors';
import { selectIsUserAgent } from 'services/session/sessionSelectors';
import findUnlockUpgradeProduct from './utils';

const selectUnlockInsuranceState = (state: RootState) => state.unlockInsurance;

export const selectIsUnlockInsuranceModalClosed = createSelector(
  [selectUnlockInsuranceState],
  (unlockState) => unlockState.isUnlockInsuranceModalClosed,
);

export const selectIsPlanUpgradeSubmitted = createSelector(
  [selectUnlockInsuranceState],
  (unlockState) => unlockState.isPlanUpgradeSubmitted,
);

export const selectUnlockInsuranceSurface = createSelector(
  [selectUnlockInsuranceState],
  (unlockState) => unlockState.surface,
);

export const selectUnlockInsuranceTargetOfferCode = createSelector(
  [selectUnlockInsuranceState],
  (unlockState) => unlockState.targetOfferCode,
);

export const selectIsUnlockInsuranceModalVisible = createSelector(
  [selectUnlockInsuranceSurface],
  (surface) => surface === 'modal' || surface === 'success',
);

export const selectIsUnlockInsuranceSuccessVisible = createSelector(
  [selectUnlockInsuranceSurface],
  (surface) => surface === 'success',
);

export const selectIsUnlockInsuranceConfirmationVisible = createSelector(
  [selectUnlockInsuranceSurface],
  (surface) => surface === 'confirmation',
);

export const selectIsUnlockInsuranceSurfaceOpen = createSelector(
  [selectUnlockInsuranceSurface],
  (surface) => surface !== 'idle',
);

export const selectUnlockInsuranceConfig = () =>
  getFromBrandConfig('unlockInsurance');

/* Brand config is read in the body rather than as a memo input, matching ctpSelectors and
   miscSelectors — safe because brand is fixed for the lifetime of a session. */
export const selectIsUnlockInsuranceBrandPlan = createSelector(
  [selectOfferPlanCode],
  (planCode) => {
    const config = getFromBrandConfig('unlockInsurance');
    return !!config && planCode === config.fromPlanCode;
  },
);

export const selectUnlockUpgradeProduct = createSelector(
  [selectAvailableOffers],
  (offers) => {
    const config = getFromBrandConfig('unlockInsurance');
    return config ? findUnlockUpgradeProduct(offers, config.toPlanCode) : null;
  },
);

/* Returns false while the reads are in flight, so callers must gate on the loaded selectors
   before treating a false as "ineligible" rather than "not yet known". */
export const selectIsUnlockInsuranceEligible = createSelector(
  [
    selectIsUnlockInsuranceBrandPlan,
    selectPlanChangeEligibility,
    selectPlanChangeEligibilityLoaded,
    selectAvailableOffersLoaded,
    selectUnlockUpgradeProduct,
    selectIsUserAgent,
    selectIsPlanUpgradeSubmitted,
  ],
  (
    isBrandPlan,
    eligibility,
    eligibilityLoaded,
    offersLoaded,
    upgradeProduct,
    isAgent,
    isPlanUpgradeSubmitted,
  ) => {
    if (!isBrandPlan || isAgent || isPlanUpgradeSubmitted) return false;
    if (!eligibilityLoaded || !offersLoaded) return false;
    return !!eligibility?.eligible && !!upgradeProduct;
  },
);
